import CasesApi from "@/api/cases";
import ClientsAPI from "@/api/clients";
import { hasDataArray, isConnectionFailure } from "@/api/connection.types";
import { getOrgInfo } from "@/api/orginfo";
import type { Case } from "@/Features/Cases/cases.types";
import type { Client } from "@/Features/Clients/clients.types";
import type { OrgInfo } from "@/utils/labels/labelUtils";
import { settleWithLimit } from "@/utils/settle-with-limit";

import type { Evidence } from "@/Features/Evidence/evidence.types";

/*
  Loads the case, client and org data a label template needs, on top of the
  evidence rows resolveSelectedEvidence returns, and builds each item's
  customizeLabel input from it.

  The app's API layer never rejects: a failed request resolves as
  `{ success: false, error }`, and a successful read has no `success: true`.
  So every response here is checked on its payload, never on a positive flag.
*/

/** Case and client lookups in flight at once. */
const LOOKUP_CONCURRENCY = 4;

/**
 * Every request here skips the global error toast: a failed run shows its own
 * error in the print dialog, and one failed lookup shouldn't add a toast on top.
 */
const SKIP_TOAST = { skipErrorToast: true };

/** Matches `{{ case.` with or without the space, in any case. */
const CASE_TOKEN = /\{\{\s*case\./i;
/** Matches `{{ client.` with or without the space, in any case. */
const CLIENT_TOKEN = /\{\{\s*client\./i;

/**
 * Whether a template has `{{ case.* }}` placeholders. `#case_number` and
 * `#case_reference` fill from the evidence row, so they don't count.
 */
export const templateUsesCaseData = (labelXml: string): boolean =>
  CASE_TOKEN.test(labelXml || "");

/** Whether a template has `{{ client.* }}` placeholders. */
export const templateUsesClientData = (labelXml: string): boolean =>
  CLIENT_TOKEN.test(labelXml || "");

/**
 * The `failedCaseIds` reason for a case the user can't open (the API answered
 * 403). Try Again can't fix it, so `refreshFailedLookups` skips these.
 */
export const NO_CASE_ACCESS = "no access to case";

export type LabelLookups = {
  orgInfo: OrgInfo;
  caseById: Record<number, Case>;
  clientByCaseId: Record<number, Partial<Client>>;
  /** Case ids whose lookup failed, each with the reason. */
  failedCaseIds: Record<number, string>;
};

export type EvidenceLabelInfo = {
  evidence: Evidence;
  case: Case | undefined;
  client: Partial<Client>;
};

const isOrgInfo = (res: unknown): res is OrgInfo =>
  typeof res === "object" &&
  res !== null &&
  !Array.isArray(res) &&
  !isConnectionFailure(res) &&
  "org_id" in res;

const loadOrgInfo = async (): Promise<OrgInfo> => {
  const res = await getOrgInfo(SKIP_TOAST);
  if (!isOrgInfo(res)) {
    throw new Error("Couldn't load the organization info.");
  }
  return res;
};

/**
 * Whether a failed response was a 403. `GET /cases` with a `case_id` answers
 * 403 to a user who isn't on that case and can't read all cases, while the
 * evidence table can still list that case's items for them. `GET /clients` has
 * no such gate today; checking it too is defensive.
 */
const isForbidden = (res: unknown): boolean =>
  isConnectionFailure(res) &&
  (res as { error?: { response?: { status?: unknown } } }).error?.response
    ?.status === 403;

type CaseLookupResult =
  | { ok: true; caseRecord?: Case; client?: Partial<Client> }
  | { ok: false; reason: string };

/**
 * One case's lookups. The case must come back; the client may be missing, and
 * a successful empty client list means the case has no client.
 */
const lookupCase = async (
  caseId: number,
  needsCase: boolean,
  needsClient: boolean
): Promise<CaseLookupResult> => {
  const [caseRes, clientRes] = await Promise.all([
    needsCase
      ? CasesApi.getCases({ case_id: caseId, data_only: false }, SKIP_TOAST)
      : undefined,
    needsClient
      ? ClientsAPI.getClients({ case_id: caseId, data_only: false }, SKIP_TOAST)
      : undefined,
  ]);

  if (isForbidden(caseRes) || isForbidden(clientRes)) {
    return { ok: false, reason: NO_CASE_ACCESS };
  }

  let caseRecord: Case | undefined;
  if (needsCase) {
    caseRecord = hasDataArray(caseRes)
      ? (caseRes.data[0] as Case | undefined)
      : undefined;
    if (!caseRecord) return { ok: false, reason: "case lookup failed" };
  }

  let client: Partial<Client> | undefined;
  if (needsClient) {
    if (!hasDataArray(clientRes)) {
      return { ok: false, reason: "client lookup failed" };
    }
    client = (clientRes.data[0] as Client | undefined) ?? {};
  }

  return { ok: true, caseRecord, client };
};

const distinctCaseIds = (items: Evidence[]): number[] =>
  Array.from(
    new Set(
      items
        .map((item) => item.case_id)
        .filter((id): id is number => id !== null && id !== undefined)
    )
  );

/**
 * Runs the case and client lookups a template needs for `caseIds`, four at a
 * time, and merges the outcome into `base`. A case that succeeds is cleared
 * from `failedCaseIds`; one that fails is put there with its reason.
 */
const runCaseLookups = async (
  base: LabelLookups,
  caseIds: number[],
  labelXml: string
): Promise<LabelLookups> => {
  const needsCase = templateUsesCaseData(labelXml);
  const needsClient = templateUsesClientData(labelXml);

  const next: LabelLookups = {
    orgInfo: base.orgInfo,
    caseById: { ...base.caseById },
    clientByCaseId: { ...base.clientByCaseId },
    failedCaseIds: { ...base.failedCaseIds },
  };

  if (!needsCase && !needsClient) return next;

  const results = await settleWithLimit(caseIds, LOOKUP_CONCURRENCY, (id) =>
    lookupCase(id, needsCase, needsClient)
  );

  results.forEach((result, index) => {
    const caseId = caseIds[index];

    const outcome: CaseLookupResult =
      result.status === "fulfilled"
        ? result.value
        : { ok: false, reason: "lookup request failed" };

    if (!outcome.ok) {
      next.failedCaseIds[caseId] = outcome.reason;
      return;
    }

    delete next.failedCaseIds[caseId];
    if (outcome.caseRecord) next.caseById[caseId] = outcome.caseRecord;
    if (outcome.client) next.clientByCaseId[caseId] = outcome.client;
  });

  return next;
};

/**
 * Loads what a label run needs besides the evidence rows: the org info (always,
 * and it throws if that fails), and the case and client records only when the
 * template has `{{ case.` or `{{ client.` placeholders, once per distinct case.
 */
export const loadLabelLookups = async (
  items: Evidence[],
  labelXml: string
): Promise<LabelLookups> => {
  const orgInfo = await loadOrgInfo();

  return runCaseLookups(
    { orgInfo, caseById: {}, clientByCaseId: {}, failedCaseIds: {} },
    distinctCaseIds(items),
    labelXml
  );
};

/**
 * Refetches only the cases whose lookups failed, for Try Again. Everything that
 * already loaded is kept, and cases the user has no access to are left as
 * failed without another request.
 */
export const refreshFailedLookups = async (
  lookups: LabelLookups,
  items: Evidence[],
  labelXml: string
): Promise<LabelLookups> => {
  const failed = distinctCaseIds(items).filter(
    (id) =>
      lookups.failedCaseIds[id] !== undefined &&
      lookups.failedCaseIds[id] !== NO_CASE_ACCESS
  );

  return runCaseLookups(lookups, failed, labelXml);
};

/**
 * The `itemInfo` `customizeLabel` fills one evidence label from. Throws when
 * the item's case lookup failed: one job can't send part of a batch, so the run
 * stops before anything is sent rather than printing blank case fields. The
 * client defaults to an empty object so client placeholders print blank
 * instead of raw.
 */
export const buildEvidenceItemInfo = (
  item: Evidence,
  lookups: LabelLookups
): EvidenceLabelInfo => {
  const reason = lookups.failedCaseIds[item.case_id];

  if (reason !== undefined) {
    const caseLabel = item.case_number || item.case_id;
    throw new Error(
      reason === NO_CASE_ACCESS
        ? `You don't have access to case ${caseLabel}`
        : `Couldn't load case data for case ${caseLabel}`
    );
  }

  return {
    evidence: item,
    case: lookups.caseById[item.case_id],
    client: lookups.clientByCaseId[item.case_id] ?? {},
  };
};
